import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { ThemeProps, SelectableThemeProps } from '@theme/base';
import { isTablet, isMobile, mq } from '@styles/breakpoints';
import AutosizeInput from 'react-input-autosize';
import { Icons } from '@model/common';
import { Pill } from '@components/search/destinations/suggested-destinations/Pill';
import { Icon } from '@components/common/generic-icon';
import { useI18NextContext } from '@components/hooks';
import { TestId } from '@components/test-ids';

const SelectionContainer = styled.div(({ theme }: ThemeProps) => ({
  background: theme.custom.colors.group2.lighter,
  paddingLeft: theme.custom.spacing.medium,
  paddingRight: theme.custom.spacing.medium,
  paddingTop: theme.custom.spacing.medium,
  [mq.medium]: {
    width: '100%'
  }
}));

const InputContainer: any = styled.div(({ theme, isSelected }: SelectableThemeProps) => ({
  background: theme.custom.colors.white,
  border: `1px solid ${isSelected ? theme.custom.colors.group1.base : theme.custom.colors.group10.light}`,
  borderRadius: 10,
  alignItems: 'center',
  display: 'flex',
  flex: '1 1 0%',
  flexWrap: 'wrap',
  padding: theme.custom.spacing.small
}));

const SearchIcon = styled(Icon)(({ theme }: ThemeProps) => ({
  fontSize: '1.8rem',
  padding: `0 ${theme.custom.spacing.small}px`
}));

const StyledInput = styled(AutosizeInput)(({ theme }: ThemeProps) => ({
  input: {
    outline: 'none',
    border: 'none',
    height: 30,
    padding: 0,
    margin: 2,
    ...(theme.custom.typography.paragraph as any),
    '::placeholder': {
      ...(theme.custom.typography.paragraph as any)
    }
  }
}));

const StyledPill = styled(Pill)({
  margin: 2
});

const Separator: any = styled.hr(({ theme }: ThemeProps) => ({
  border: 'none',
  borderTop: `1px solid ${theme.custom.colors.group10.light}`,
  margin: `${theme.custom.spacing.medium}px -${theme.custom.spacing.medium}px ${theme.custom.spacing.medium}px`,
  marginBottom: 0
}));

export interface HeadingProps {
  input: string;
  values: Array<string>;
  placeholder: string;
  onChange: (payload: string) => void;
  onClear: (payload: Array<string>) => void;
}

export const Heading = ({ input, values, placeholder, onChange, onClear }: HeadingProps) => {
  const ref: any = useRef();
  const t: any = useI18NextContext();
  const inputPlaceholder: string = values.length ? 'search__destination--add-another' : placeholder;
  const handleOnChange = (event: any) => onChange(event.target.value);
  const handleOnClear = (option: string) => onClear(values.filter((value: string) => value !== option));
  const renderValues = values.map((option: string) => (
    <StyledPill key={option} display={option} value={option} onClear={handleOnClear} />
  ));
  useEffect(() => {
    if (!isTablet() && !isMobile()) {
      ref.current?.focus();
    }
  }, [values]);

  return (
    <SelectionContainer data-testid={TestId.dealFinder.airports.heading}>
      <InputContainer onClick={() => ref.current?.focus()} isSelected>
        <SearchIcon name={Icons.SEARCH} />
        {renderValues}
        <StyledInput ref={ref} value={input} onChange={handleOnChange} placeholder={t(inputPlaceholder)} />
      </InputContainer>
      <Separator />
    </SelectionContainer>
  );
};
